import React, {useCallback, useRef,} from "react";
import Feature, {FeatureLike} from "ol/Feature";
import {Fill, Icon, Stroke, Style, Text} from "ol/style";
import {colorsHex, MINT, NEUTRAL, PRIMARY} from "../../../common/consts.common";
import {SelectEvent} from "ol/interaction/Select";
import {showMessage} from "../../../common/message";
import {useTranslation} from "react-i18next";
import {MessageInstance} from "antd/es/message/interface";
import {getRandomNumber} from "../../../common/fns";
import CircleStyle from "ol/style/Circle";
import {TPlanMarkerProperty} from "../../organisms/plan/PlanMap";
import {StyleLike} from "ol/style/Style";
import {Options as IconOptions} from "ol/style/Icon";
import {renderToStaticMarkup} from "react-dom/server";
import {Marker} from "../../../assets/svgs/marker";

export type TMarkerProperty<T = any> = {
  code: string;
  hovered?: boolean;
  highlighted: boolean;
  disabled?: boolean;
  shipping?: T;
  color?: string;
}

type TStyleCache = {
  [key: string]: StyleLike
};

type TColorCache = {
  [key: string]: string
};

export function useMapFunctions<T>(onSelectShippings: (shippings: T[]) => void, onReset?: () => void, messageApi?: MessageInstance) {
  const selectedFeatureRef = useRef<Feature[]>([]);
  const {t} = useTranslation("shipping");
  const colorCache = useRef<TColorCache>({});
  const clusterCache = useRef<TStyleCache>({});
  const unusedColorsRef = useRef([...colorsHex]); // copy
  
  const getSelectedShippings = useCallback(() => {
    const shippings: T[] = [];
    selectedFeatureRef.current && selectedFeatureRef.current.forEach((f) => {
      const properties = f.getProperties() as TMarkerProperty<T>;
      console.log("getSelectedShippings", properties, f)
      if (properties.shipping) shippings.push(properties.shipping);
    });
    return shippings;
  }, []);
  
  const addToSelectedShippings = useCallback((feature: Feature, properties: TMarkerProperty<T>) => {
    if (!properties?.disabled) {
      selectedFeatureRef.current.push(feature);
      feature.setProperties({...properties, highlighted: true});
    }
    console.log("addToSelectedShippings feature", selectedFeatureRef.current, feature);
  }, []);
  
  const removeFromSelectedShippings = useCallback((feature: Feature, properties: TMarkerProperty<T>) => {
    if (!properties?.disabled) {
      feature.setProperties({...properties, highlighted: false});
      selectedFeatureRef.current = selectedFeatureRef.current.filter((selected) => {
        return selected !== feature;
      });
    }
    console.log("filter feature", selectedFeatureRef.current, feature);
  }, []);
  
  const isClusterHighlighted = useCallback((features: Feature[]) => {
    let highlighted = false;
    for (let i in features) {
      const f = features[i];
      const properties = f.getProperties() as TMarkerProperty<T>;
      console.log("handleClick", properties);
      if (properties.highlighted) {
        highlighted = true;
        break;
      }
    }
    return highlighted;
  }, []);
  
  const getMarkerIcon = (color?: string, strokeColor?: string): IconOptions => {
    let fillColor = (color ? color : '#' + PRIMARY), centerColor = `rgba(0, 0, 0, 0.40)`;
    console.log("getMarkerIcon", strokeColor, fillColor, centerColor)
    const svgString = renderToStaticMarkup(<Marker fillColor={fillColor} strokeColor={strokeColor || "#fff"}
                                                   centerColor={strokeColor || centerColor} />);
    const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
    return ({
      src: svgDataUrl, scale: 1, anchorOrigin: "bottom-left",
    });
  }
  
  const createClusterStyle = (color: string, size: number, radius: number, alpha: number = 99, strokeColor?: string) => {
    if (size === 1) {
      return new Style({
        image: new Icon(getMarkerIcon(color, strokeColor)),
      });
    }
    else {
      return new Style({
        image: new CircleStyle({
          radius, stroke: new Stroke({
            width: 2, color: strokeColor || '#fff',
          }), fill: new Fill({
            color: color ? `${color}${alpha}` : "#3399CC",
          }),
        }), text: new Text({
          font: "bold 13px Spoqa Han Sans Neo", text: size.toString(), fill: new Fill({
            color: "#fff",
          }),
        }),
      })
    }
  }
  
  function round4(n: number) {
    return Math.round(n * 100000);
  }
  
  const getCircleStyleFromRes = (resolution: number = 13) => {
    if (resolution < 8) { // was 130 in EPSG:3857
      return {radius: 16, alpha: 95};
    }
    else if (resolution < 15) { // was 45
      return {radius: 20, alpha: 85};
    }
    else if (resolution < 22) { // was 90
      return {radius: 26, alpha: 75};
    }
    else if (resolution < 30) { // was 90
      return {radius: 32, alpha: 65};
    }
    return {radius: 37, alpha: 55};
  }
  
  const getRandomColor = useCallback((code: string, shouldFilter: boolean = true) => {
    console.log("getRandomColorSector code: ", code, "color:", colorCache.current[code]);
    if (!colorCache.current[code]) {
      const random = getRandomNumber(shouldFilter ? [MINT, PRIMARY] : []);
      // @ts-ignore
      let randomColor = "#" + random.toString(16);
      randomColor = randomColor.padEnd(7, "0");
      colorCache.current[code] = randomColor;
    }
    return colorCache.current[code];
  }, []);
  
  const getColor = useCallback((unusedColors: string[], code: string, shouldFilter: boolean = true) => {
    console.log("getRandomColorSector code: ", code, "color:", colorCache.current[code]);
    if (!colorCache.current[code]) {
      // if list is empty → return random color
      if (unusedColors.length === 0) {
        colorCache.current[code] = getRandomColor(code, shouldFilter);
      }
      else {
        // take one color from the list
        colorCache.current[code] = unusedColors.shift() as string;
      }
    }
    return colorCache.current[code];
  }, []);
  
  const isHighlightedOrDisabled = (features: Feature[]) => {
    let highlighted = false, disabled = true;
    for (let i = 0; features && i < features.length; i++) {
      const feature = features[i];
      const properties = feature.getProperties() as TPlanMarkerProperty;
      console.log("cluster properties", properties)
      if (properties.highlighted) highlighted = true; //하나라도 highlighed이면 클러스터 전체를 higlighted으로 강조
      if (!properties.disabled) disabled = false; //전체가 다 disabled이여야 클러스터 전체를 disabled으로 강조
    }
    return {disabled, highlighted};
  }
  
  const clusterStyle = useCallback((feature: FeatureLike, resolution: number) => {
    const fixedRes = round4(resolution);
    const features = feature.get("features");
    const size = features.length;
    if (size > 0) {
      const {radius, alpha} = getCircleStyleFromRes(fixedRes);
      const {disabled, highlighted} = isHighlightedOrDisabled(features)
      const clusterId = features[0].get("name");
      if (disabled) {
        const cacheKey = fixedRes + "disabled" + clusterId + size;
        if (!clusterCache.current[cacheKey]) {
          const color = getColor(unusedColorsRef.current, clusterId);
          clusterCache.current[cacheKey] = createClusterStyle("#" + NEUTRAL, size, radius, 99, color);
        }
        return clusterCache.current[cacheKey]
      }
      else if (highlighted) {
        const key = "highlighted" + fixedRes + size;
        if (!clusterCache.current[key]) {
          clusterCache.current[key] = createClusterStyle("#" + MINT, size, radius);
        }
        return clusterCache.current[key]
      }
      const cacheKey = fixedRes + clusterId + size;
      console.log("clusterStyle key:", clusterId, cacheKey, !clusterCache.current[cacheKey])
      if (!clusterCache.current[cacheKey]) {
        const color = getColor(unusedColorsRef.current, clusterId);
        clusterCache.current[cacheKey] = createClusterStyle(color, size, radius, alpha);
      }
      return clusterCache.current[cacheKey];
    }
  }, []);
  
  const clusterOverStyle = (feature: FeatureLike, resolution: number) => {
    const fixedRes = round4(resolution);
    const clusterId = feature.get("name") || "";
    const size = feature.get("features").length;
    const color = "#" + PRIMARY;
    const cacheKey = color + fixedRes + size;
    console.log("clusterOverStyle id:", clusterId, "res: ", fixedRes, "key:", cacheKey, !clusterCache.current[cacheKey])
    if (!clusterCache.current[cacheKey]) {
      const {radius} = getCircleStyleFromRes(fixedRes);
      clusterCache.current[cacheKey] = createClusterStyle(color, size, radius);
    }
    return clusterCache.current[cacheKey];
  };
  
  const handleClickCluster = useCallback((selected: Feature[]) => {
    if (selected?.length) {
      let features: Feature[] = selected[0].get("features");
      if (!features?.length) {
        //is not cluster
        features = selected;
      }
      const highlighted = isClusterHighlighted(features);
      let isWarningShown = false;
      features && features.forEach((f: Feature) => {
        const properties = f.getProperties() as TMarkerProperty<T>;
        if (highlighted) { //클러스터에 선택된 물품이 있을 시 클러스터 속하는 모든 물품 미선택으로 변경
          if (properties.highlighted) removeFromSelectedShippings(f, properties);
        }
        else {
          if (!properties.highlighted) {
            addToSelectedShippings(f, properties);
            if (properties.disabled) { //분료완료된 물품 선택 불가
              if (!isWarningShown && messageApi) {
                showMessage(messageApi, t("Please, select a shipping not sorted"))
                isWarningShown = true;
              }
            }
          }
        }
      });
      const shippings = getSelectedShippings();
      console.log("handleClickCluster", selected, shippings);
      onSelectShippings(shippings);
    }
  }, [
    addToSelectedShippings,
    isClusterHighlighted,
    getSelectedShippings,
    messageApi,
    onSelectShippings,
    removeFromSelectedShippings,
    t
  ]);
  
  const handleMouseOverCluster = useCallback((selected: Feature[], deselected: Feature[], event: SelectEvent, onMouseOver, onMouseOut) => {
    console.log("handleMouseOverCluster features", selected);
    const shippings: T[] = [];
    let selectedFeatures: Feature[] = [];
    if (selected?.length) {
      selectedFeatures = selected[0].get("features");
      if (!selectedFeatures?.length) {
        //is not cluster
        selectedFeatures = selected;
      }
      selectedFeatures && selectedFeatures.forEach((feature) => {
        const properties = feature.getProperties() as TMarkerProperty<T>;
        console.log("handleMouseOverCluster properties", properties);
        if (properties.shipping) {
          feature.setProperties({...properties, hovered: true});
          shippings.push(properties.shipping);
        }
        if (shippings.length > 0) {
          onMouseOver({
            shippings, position: event.mapBrowserEvent.coordinate,
          });
        }
      });
    }
    if (deselected?.length) {
      let deselectedFeatures: Feature[] = deselected[0].get("features");
      if (!deselectedFeatures?.length) {
        //is not cluster
        deselectedFeatures = deselected;
      }
      if (selectedFeatures[0] === deselectedFeatures[0]) return;
      onMouseOut();
      console.log("handleMouseOverCluster desect features", deselectedFeatures);
      deselectedFeatures && deselectedFeatures.forEach((feature) => {
        const properties = feature.getProperties() as TMarkerProperty<T>;
        if (!properties.highlighted) {
          feature.setProperties({...properties, hovered: false});
        }
      });
    }
    console.log("onMouseOverCluster1", shippings);
  }, []);
  
  const handleReset = useCallback(() => {
    selectedFeatureRef.current && selectedFeatureRef.current.forEach((feature) => {
      const properties = feature.getProperties() as TMarkerProperty<T>;
      removeFromSelectedShippings(feature, properties);
    });
    onReset && onReset();
  }, [onReset, removeFromSelectedShippings]);
  
  console.log("usemap render", colorCache);
  return {
    handleMouseOverCluster,
    handleClickCluster,
    handleReset,
    addToSelectedShippings,
    getSelectedShippings,
    getColor,
    clusterStyle,
    clusterOverStyle,
  }
}
